import { useState, useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Ticket } from '../types'
import { useAuth } from '../hooks/useAuth'
import { loadTickets, updateTicketStatus } from '../lib/api'
import TicketList from '../components/TicketList'
import TicketsTable from '../components/TicketsTable'
import { useAlert } from '../contexts/AlertContext'
import { comparePersonNames } from '../utils/personNameSort'
import './ExecutorView.css'

type AssignmentScopeFilter = 'all' | 'mine' | 'external' | 'others'

function isExternalFirmaTicket(ticket: Ticket): boolean {
  const name = (ticket.executor_name || '').trim().toLowerCase()
  return name === 'externí firma' || name === 'externi firma'
}

function ExecutorView() {
  const { user, loading: authLoading } = useAuth()
  const { showAlert } = useAlert()
  const [searchParams, setSearchParams] = useSearchParams()
  const deepLinkHandled = useRef<string | null>(null)
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards')
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [assignmentScope, setAssignmentScope] = useState<AssignmentScopeFilter>('all')
  const [executorPersonFilter, setExecutorPersonFilter] = useState<string | null>(null)
  const [completeTicketId, setCompleteTicketId] = useState<string | null>(null)
  const [completionComment, setCompletionComment] = useState('')
  const [completing, setCompleting] = useState(false)
  const [highlightId, setHighlightId] = useState<string | null>(null)

  useEffect(() => {
    if (user && !authLoading) {
      loadTicketsData()
    } else if (!authLoading && !user) {
      setLoading(false)
    }
  }, [user, authLoading])

  useEffect(() => {
    const id = searchParams.get('id')
    if (!id || loading) return
    if (deepLinkHandled.current === id) return
    deepLinkHandled.current = id

    const found = tickets.find((t) => t.id === id)
    if (found) {
      setHighlightId(id)
      setAssignmentScope('mine')
      if (found.status === 'in_progress' || found.status === 'completed') {
        setStatusFilter(found.status)
      } else {
        setStatusFilter(null)
      }
      window.setTimeout(() => {
        const el = document.getElementById(`ticket-${id}`)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 150)
    } else {
      showAlert('Žádost nenalezena nebo již není ve vaší frontě.', 'error')
    }
    const next = new URLSearchParams(searchParams)
    next.delete('id')
    setSearchParams(next, { replace: true })
  }, [searchParams, loading, tickets, setSearchParams, showAlert])

  const loadTicketsData = async () => {
    if (!user) return
    
    try {
      setLoading(true)
      // role=executor: vlastní; správce budov navíc všechny budov žádosti; IT + Externí firma
      const data = await loadTickets(user.id, 'executor', user.admin_type)
      setTickets(data)
    } catch (error) {
      console.error('Chyba při načítání žádostí:', error)
      showAlert('Chyba při načítání žádostí', 'error')
    } finally {
      setLoading(false)
    }
  }

  const openCompleteModal = (ticketId: string) => {
    setCompleteTicketId(ticketId)
    setCompletionComment('')
  }

  const closeCompleteModal = () => {
    if (completing) return
    setCompleteTicketId(null)
    setCompletionComment('')
  }

  const handleConfirmComplete = async () => {
    if (!completeTicketId) return
    setCompleting(true)
    try {
      await updateTicketStatus(
        completeTicketId,
        'completed',
        undefined,
        completionComment.trim(),
      )
      setCompleteTicketId(null)
      setCompletionComment('')
      await loadTicketsData()
      showAlert('Žádost byla označena jako hotovo', 'success')
    } catch (error: any) {
      console.error('Chyba při dokončení žádosti:', error)
      showAlert(error.message || 'Chyba při dokončení žádosti', 'error')
    } finally {
      setCompleting(false)
    }
  }

  const ticketForComplete = useMemo(
    () => (completeTicketId ? tickets.find((t) => t.id === completeTicketId) ?? null : null),
    [completeTicketId, tickets],
  )

  const isSpravceBudov = Boolean(
    user && user.role === 'admin' && user.admin_type === 'spravce_budov',
  )
  const canFilterAssignmentScope = Boolean(
    user &&
      user.role === 'admin' &&
      (user.admin_type === 'spravce_budov' || user.admin_type === 'spravce_it'),
  )

  const assignedTicketsRaw = useMemo(
    () => tickets.filter(t => t.status === 'in_progress' || t.status === 'completed'),
    [tickets],
  )

  const userId = user?.id ?? ''

  const executorOptions = useMemo(() => {
    const counts = new Map<string, { name: string; count: number }>()
    for (const t of assignedTicketsRaw) {
      if (!t.executor_id) continue
      const name = (t.executor_name || '').trim() || t.executor_id.slice(0, 8)
      const prev = counts.get(t.executor_id)
      if (prev) {
        prev.count += 1
      } else {
        counts.set(t.executor_id, { name, count: 1 })
      }
    }
    return Array.from(counts.entries())
      .map(([id, { name, count }]) => ({ id, name, count }))
      .sort((a, b) => comparePersonNames({ name: a.name }, { name: b.name }))
  }, [assignedTicketsRaw])

  const assignedTickets = useMemo(() => {
    let list = assignedTicketsRaw

    if (canFilterAssignmentScope && assignmentScope !== 'all' && userId) {
      if (assignmentScope === 'mine') {
        list = list.filter(t => t.executor_id === userId)
      } else if (assignmentScope === 'external') {
        list = list.filter(t => isExternalFirmaTicket(t))
      } else {
        // ostatní řešitelé (ne já, ne Externí firma)
        list = list.filter(
          t =>
            t.executor_id != null &&
            t.executor_id !== userId &&
            !isExternalFirmaTicket(t),
        )
      }
    }

    if (executorPersonFilter) {
      list = list.filter(t => t.executor_id === executorPersonFilter)
    }

    return list
  }, [
    assignedTicketsRaw,
    assignmentScope,
    canFilterAssignmentScope,
    userId,
    executorPersonFilter,
  ])

  const setScopeFilter = (scope: AssignmentScopeFilter) => {
    setAssignmentScope(scope)
    setExecutorPersonFilter(null)
  }

  const filteredTickets = useMemo(
    () =>
      statusFilter
        ? assignedTickets.filter(t => t.status === statusFilter)
        : assignedTickets,
    [statusFilter, assignedTickets],
  )

  const inProgressTickets = useMemo(
    () => filteredTickets.filter(t => t.status === 'in_progress'),
    [filteredTickets],
  )
  const completedTickets = useMemo(
    () => filteredTickets.filter(t => t.status === 'completed'),
    [filteredTickets],
  )

  // Показываем загрузку, пока проверяем аутентификацию или загружаем данные
  if (authLoading || (loading && !user)) {
    return (
      <div className="executor-view">
        <div className="loading">Načítání...</div>
      </div>
    )
  }

  // Если пользователь не аутентифицирован, не показываем контент
  if (!user) {
    return null
  }

  return (
    <div className="executor-view">
      <div className="executor-header">
        <h2>Přiřazené žádosti ({assignedTickets.length})</h2>
      </div>

      {!loading && (
        <>
          {canFilterAssignmentScope && (
            <div className="executor-assignment-filter" role="group" aria-label="Rozsah zobrazení úkolů">
              <span className="executor-assignment-filter-label">Zobrazit:</span>
              <button
                type="button"
                className={`view-button ${assignmentScope === 'all' && !executorPersonFilter ? 'active' : ''}`}
                onClick={() => setScopeFilter('all')}
              >
                Všechny
              </button>
              <button
                type="button"
                className={`view-button ${assignmentScope === 'mine' && !executorPersonFilter ? 'active' : ''}`}
                onClick={() => setScopeFilter('mine')}
              >
                Pouze mé
              </button>
              <button
                type="button"
                className={`view-button ${assignmentScope === 'external' && !executorPersonFilter ? 'active' : ''}`}
                onClick={() => setScopeFilter('external')}
              >
                Externí firma
              </button>
              {isSpravceBudov && (
                <button
                  type="button"
                  className={`view-button ${assignmentScope === 'others' && !executorPersonFilter ? 'active' : ''}`}
                  onClick={() => setScopeFilter('others')}
                >
                  Ostatní řešitelé
                </button>
              )}
            </div>
          )}
          {isSpravceBudov && executorOptions.length > 0 && (
            <div className="executor-person-filter">
              <label className="executor-assignment-filter-label" htmlFor="executor-person-select">
                Řešitel:
              </label>
              <select
                id="executor-person-select"
                className="executor-person-select"
                value={executorPersonFilter ?? ''}
                onChange={(e) => {
                  const value = e.target.value
                  if (!value) {
                    setExecutorPersonFilter(null)
                    return
                  }
                  setAssignmentScope('all')
                  setExecutorPersonFilter(value)
                }}
              >
                <option value="">Všichni řešitelé</option>
                {executorOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name} ({opt.count})
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="admin-stats">
            <div 
              className={`stat-card ${statusFilter === 'in_progress' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'in_progress' ? null : 'in_progress')}
              style={{ cursor: 'pointer' }}
            >
              <h3>V řešení</h3>
              <p className="stat-number">{assignedTickets.filter(t => t.status === 'in_progress').length}</p>
            </div>
            <div 
              className={`stat-card ${statusFilter === 'completed' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'completed' ? null : 'completed')}
              style={{ cursor: 'pointer' }}
            >
              <h3>Dokončeno</h3>
              <p className="stat-number">{assignedTickets.filter(t => t.status === 'completed').length}</p>
            </div>
            <div 
              className={`stat-card ${statusFilter === null ? 'active' : ''}`}
              onClick={() => setStatusFilter(null)}
              style={{ cursor: 'pointer' }}
            >
              <h3>Celkem</h3>
              <p className="stat-number">{assignedTickets.length}</p>
            </div>
          </div>

          <div className="view-controls">
            <button
              className={`view-button ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
            >
              📋 Karty
            </button>
            <button
              className={`view-button ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
            >
              📊 Tabulka
            </button>
          </div>
        </>
      )}

      {loading ? (
        <div className="loading">Načítání...</div>
      ) : assignedTicketsRaw.length === 0 ? (
        <div className="empty-state">
          <p>Nemáte žádné přiřazené žádosti</p>
        </div>
      ) : assignedTickets.length === 0 ? (
        <div className="empty-state">
          <p>Pro zvolený filtr nejsou žádné žádosti</p>
        </div>
      ) : viewMode === 'table' ? (
        <TicketsTable 
          tickets={filteredTickets}
          onComplete={openCompleteModal}
          showCompleteButton={true}
          filterType="executor"
        />
      ) : statusFilter ? (
        <>
          {statusFilter === 'in_progress' && (
            <div className="tickets-section">
              <h2>Probíhající žádosti ({inProgressTickets.length})</h2>
              {inProgressTickets.length > 0 ? (
                <TicketList 
                  tickets={inProgressTickets} 
                  onComplete={openCompleteModal}
                  showCompleteButton={true}
                  highlightedTicketId={highlightId}
                />
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné žádosti v řešení</p>
              )}
            </div>
          )}
          {statusFilter === 'completed' && (
            <div className="tickets-section">
              <h2>Dokončené žádosti ({completedTickets.length})</h2>
              {completedTickets.length > 0 ? (
                <TicketList 
                  tickets={completedTickets} 
                  onComplete={openCompleteModal}
                  showCompleteButton={false}
                  highlightedTicketId={highlightId}
                />
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné dokončené žádosti</p>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="tickets-section">
            <h2>Žádosti v řešení ({assignedTickets.filter(t => t.status === 'in_progress').length})</h2>
            {inProgressTickets.length > 0 ? (
              <TicketList 
                tickets={inProgressTickets} 
                onComplete={openCompleteModal}
                showCompleteButton={true}
                highlightedTicketId={highlightId}
              />
            ) : (
              <p style={{ padding: '1rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Žádné žádosti v řešení</p>
            )}
          </div>

          <div className="tickets-section">
            <h2>Dokončené žádosti ({assignedTickets.filter(t => t.status === 'completed').length})</h2>
            {completedTickets.length > 0 ? (
              <TicketList 
                tickets={completedTickets} 
                onComplete={openCompleteModal}
                showCompleteButton={false}
                highlightedTicketId={highlightId}
              />
            ) : (
              <p style={{ padding: '1rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Žádné dokončené žádosti</p>
            )}
          </div>
        </>
      )}

      {completeTicketId && (
        <div className="executor-complete-overlay" onClick={closeCompleteModal}>
          <div
            className="executor-complete-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="executor-complete-title"
          >
            <h3 id="executor-complete-title">Označit jako hotovo</h3>
            {ticketForComplete ? (
              <p className="executor-complete-meta">
                Žádost #{ticketForComplete.id.slice(0, 8)}
                {ticketForComplete.building_name ? ` — ${ticketForComplete.building_name}` : ''}
              </p>
            ) : null}
            <label className="executor-complete-label">
              Komentář k provedené práci
              <textarea
                value={completionComment}
                onChange={(e) => setCompletionComment(e.target.value)}
                rows={4}
                maxLength={2000}
                placeholder="Popište, co bylo provedeno…"
                disabled={completing}
              />
            </label>
            <div className="executor-complete-actions">
              <button
                type="button"
                className="executor-complete-confirm"
                disabled={completing}
                onClick={() => void handleConfirmComplete()}
              >
                {completing ? 'Ukládám…' : 'Hotovo'}
              </button>
              <button
                type="button"
                className="executor-complete-cancel"
                disabled={completing}
                onClick={closeCompleteModal}
              >
                Zrušit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ExecutorView




